import { dequal as dequalLite } from "dequal/lite";
import isNumber from "lodash-es/isNumber";
import isObject from "lodash-es/isObject";
import isString from "lodash-es/isString";

interface ForEachPrimitiveValueArgs {
  // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  target: Record<string, any> | unknown[] | null;
  // Note (Chance 2023-07-14) Consider improving types and update `any`s ... but
  // maybe this one isn't worth it since it'll be ridiculously overloaded
  // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  fn: (value: unknown, key: string, newTarget: Record<string, any>) => void;
  valueConversionFunction?: (
    // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
    transformValue: Record<string, any> | unknown[],
    // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  ) => Record<string, any> | unknown[];
}
/** There's probably a bug somewhere here since it assumes target is initially an object */
export function forEachPrimitiveValue({
  target,
  fn,
  valueConversionFunction,
}: ForEachPrimitiveValueArgs) {
  if (!target) {
    return;
  }

  for (const [key, value] of Object.entries(target)) {
    if (isObject(value)) {
      const newTarget = valueConversionFunction
        ? valueConversionFunction(value)
        : value;
      forEachPrimitiveValue({ target: newTarget, fn });
    }
    if (isString(value) || isNumber(value)) {
      fn(value, key, target);
    }
  }
}

// https://github.com/facebook/react/blob/main/packages/shared/shallowEqual.js
export function shallowEqual(objA: unknown, objB: unknown) {
  if (Object.is(objA, objB)) {
    return true;
  }

  if (
    typeof objA !== "object" ||
    objA === null ||
    typeof objB !== "object" ||
    objB === null
  ) {
    return false;
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  // Test for A's keys different from B.
  for (let i = 0; i < keysA.length; i++) {
    const currentKey = keysA[i]!;
    if (
      !Object.hasOwnProperty.call(objB, currentKey) ||
      // @ts-ignore: lost refinement of `objB`
      !Object.is(objA[currentKey], objB[currentKey])
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Internally we use `dequal` for deep equality checks. It benchmarks faster
 * than lodash and most other libraries.
 *
 * This function is an alias for `dequal`'s "lite" module. It does not check
 * equality for `Map` or `Set` values. If you need to compare those, use
 * `deepEqualHeavy`.
 *
 * @see https://github.com/lukeed/dequal#modes
 */
export const deepEqual = dequalLite;

/**
 * Creates a new object with specified keys omitted.
 *
 * @param obj - The source object
 * @param keysToOmit - Array of keys to omit from the result
 * @returns A new object without the specified keys
 */
export function omit<
  // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  ObjectType extends Record<string, any>,
  KeyType extends keyof ObjectType,
>(obj: ObjectType, keysToOmit: KeyType[]): Omit<ObjectType, KeyType> {
  const result = { ...obj };
  for (const key of keysToOmit) {
    delete result[key];
  }
  return result;
}

export function filterUndefined<ObjectType extends Record<string, unknown>>(
  data: ObjectType,
) {
  // Legacy cast - may or may not be necessary
  // eslint-disable-next-line replo/no-as-cast
  return Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined),
  ) as Partial<ObjectType>;
}

export function toNullableIfDefined<ValueType>(
  value: ValueType | undefined,
): ValueType | null | undefined {
  return value === undefined ? undefined : value ?? null;
}
